/* global $ */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IS_CUSTOM_PARTICIPANT_EVERYONE, setCustomParticipantEveryone, setCustomParticipantVisible } from '../../../base/conference';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { truncateText } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { Icon, IconMenuThumb, IconModeratorPin, IconUserPinned } from '../../../base/icons';
import { getParticipantById, isLocalParticipantModerator, pinParticipant } from '../../../base/participants';
import { BaseIndicator } from '../../../base/react';
import { connect } from '../../../base/redux';
import { exitTileViewIfNeeded } from '../../../conference/filmStripViewManager';
import { getFirstName } from '../../../display-name';
import { setLabelsVisible } from '../../../toolbox/actions.web';
import { LAYOUTS } from '../../../video-layout';


declare var interfaceConfig: Object;
const visibleButton = new Set(interfaceConfig.TOOLBAR_BUTTONS);


/**
 * Implements a React {@link Component} which represents the large video (a.k.a.
 * The conference participant who is on the local stage) on Web/React.
 *
 * @augments Component
 */
function SharedThumbnail({
    _isCustomParticipantVisible,
    whiteBoardVisible,
    etherPadVisible,
    IframeSharingParticipant,
    dispatch,
    filmStripView,
    _isPersonModerator,
    _visibleButtons,
    _isCustomParticipantEveryone,
    conference,
    _isPictureInPictureEnabled,
    whiteBoardParticipantId,
    etherpadParticipantId
}: any) {
    const containerRef = useRef(null);
    const videoContainerRef = useRef(null);
    const contextMenuInitialized = useRef(false);
    const { t } = useTranslation();

    const participantColor = {
        backgroundColor: IframeSharingParticipant?.color
    };

    // Get the participant ID based on whether it's whiteboard or etherpad
    const participantID = whiteBoardVisible ? whiteBoardParticipantId : etherpadParticipantId;

    const _renderIframeThumbnail = useCallback(() => {
        if (filmStripView !== LAYOUTS.TILE_VIEW) {
            dispatch(setCustomParticipantVisible(!_isCustomParticipantVisible));
        }
        dispatch(pinParticipant(null, false));
        exitTileViewIfNeeded(dispatch, filmStripView);
        _isCustomParticipantVisible || _isCustomParticipantEveryone ? dispatch(setLabelsVisible(true)) : dispatch(setLabelsVisible(false));
    }, [ dispatch, _isCustomParticipantVisible, _isCustomParticipantEveryone, filmStripView ]);

    const _renderIframeThumbnailModeratorClick = useCallback(() => {
        dispatch(setCustomParticipantEveryone(!_isCustomParticipantEveryone));
        dispatch(pinParticipant(null, true));
        const myId = conference.myUserId();

        conference.sendMessage({
            type: IS_CUSTOM_PARTICIPANT_EVERYONE,
            action: !_isCustomParticipantEveryone,
            senderId: myId
        });

        _isCustomParticipantEveryone ? dispatch(setLabelsVisible(true)) : dispatch(setLabelsVisible(false));
    }, [ dispatch, _isCustomParticipantEveryone ]);

    const displayLabel = useMemo(() => {
        const participantName = IframeSharingParticipant?.local ? 'You' : getFirstName(IframeSharingParticipant?.name || 'Moderator');
        const textlimit = window.innerWidth < 1024 || _isPictureInPictureEnabled ? 10 : 26;
        const translatedText = etherPadVisible ? t('dialog.livepadSharingUser', { displayName: participantName }) : t('dialog.whiteboardSharingUser', { displayName: participantName });
        const displayNameText = truncateText(translatedText, textlimit);

        return displayNameText;
    }, [ IframeSharingParticipant?.name, etherPadVisible, whiteBoardVisible, _isPictureInPictureEnabled ]);

    const fullDisplayName = useMemo(() => {
        const participantName = IframeSharingParticipant?.name || 'Moderator';
        const translatedText = etherPadVisible ? t('dialog.livepadSharingUser', { displayName: participantName }) : t('dialog.whiteboardSharingUser', { displayName: participantName });

        return translatedText;
    }, [ IframeSharingParticipant?.name, etherPadVisible, whiteBoardVisible ]);

    const _renderTopIndicators = useMemo(() => (
        <div>
            {!_isCustomParticipantEveryone && _isCustomParticipantVisible && (
                <BaseIndicator
                    className = 'indicator show-inline'
                    icon = { IconUserPinned }
                    iconClassName = 'indicatoricon pipstroke'
                    iconSize = { '12px' }
                    tooltipKey = { t('videothumbnail.userispinned') }
                    tooltipPosition = { 'bottom-left' } />
            )}
            {_isCustomParticipantEveryone && (
                <BaseIndicator
                    className = 'indicator show-inline'
                    icon = { IconModeratorPin }
                    iconClassName = 'indicatoricon pipstroke'
                    iconSize = { '12px' }
                    tooltipKey = { t('videothumbnail.pinByModerator') }
                    tooltipPosition = { 'bottom-left' } />
            )}
        </div>
    ), [ _isCustomParticipantVisible, _isCustomParticipantEveryone ]);

    const content = useMemo(() => (
        <div className = 'popupmenu__item_dialog'>
            {_isPersonModerator && _visibleButtons.has('moderator-pin') && (
                <li className = 'popupmenu__item private_msg_box'>
                    <button
                        className = 'popupmenu__link'
                        onClick = { _renderIframeThumbnailModeratorClick }>
                        <span className = 'popupmenu__icon'>
                            <Icon src = { IconModeratorPin } />
                        </span>
                        <span className = 'popupmenu__text'>
                            {_isCustomParticipantEveryone
                                ? t('videothumbnail.unpinEveryone') : t('videothumbnail.pinEveryone')
                            }
                        </span>
                    </button>
                </li>
            )}

            { _visibleButtons.has('pin-user') && <li className = 'popupmenu__item private_msg_box'>
                <button
                    className = 'popupmenu__link'
                    onClick = { _renderIframeThumbnail }>
                    <span className = 'popupmenu__icon'>
                        <Icon src = { IconUserPinned } />
                    </span>
                    <span className = 'popupmenu__text'>
                        {_isCustomParticipantVisible ? t('videothumbnail.unpinMe') : t('videothumbnail.pinMe')}
                    </span>
                </button>
            </li>}
        </div>
    ));

    // Build and initialize jQuery contextMenu
    useEffect(() => {
        // Only initialize if whiteboard or etherpad is visible
        if (!whiteBoardVisible && !etherPadVisible) {
            return;
        }

        if (typeof $ === 'undefined' || typeof $.contextMenu !== 'function') {
            return;
        }

        const selector = '#sharedThumbnailContainer';

        // Destroy existing context menu before recreating
        if (contextMenuInitialized.current) {
            try {
                $.contextMenu('destroy', selector);
            } catch {
                // Ignore errors when destroying
            }
        }

        // Build context menu items
        const contextMenuItems = {};

        // Add Pin/Unpin option
        contextMenuItems.pin = {
            name: _isCustomParticipantVisible ? t('videothumbnail.unpinMe', {
                translationKey: 'videothumbnail.unpinMe'
            }) : t('videothumbnail.pinMe', {
                translationKey: 'videothumbnail.pinMe'

            }),
            callback: () => {
                if (filmStripView !== LAYOUTS.TILE_VIEW) {
                    dispatch(setCustomParticipantVisible(!_isCustomParticipantVisible));
                }

                dispatch(pinParticipant(participantID || null, false));

                exitTileViewIfNeeded(dispatch, filmStripView);

                if (_isCustomParticipantVisible || _isCustomParticipantEveryone) {
                    dispatch(setLabelsVisible(true));
                } else {
                    dispatch(setLabelsVisible(false));
                }
            }
        };

        // Add Pin Everyone option (only for moderators)
        if (_isPersonModerator && _visibleButtons.has('moderator-pin')) {
            contextMenuItems.pinEveryone = {
                name: _isCustomParticipantEveryone ? t('videothumbnail.unpinEveryone') : t('videothumbnail.pinEveryone'),
                callback: () => {
                    dispatch(setCustomParticipantEveryone(!_isCustomParticipantEveryone));
                    dispatch(pinParticipant(null, true));
                    const myId = conference.myUserId();

                    conference.sendMessage({
                        type: IS_CUSTOM_PARTICIPANT_EVERYONE,
                        action: !_isCustomParticipantEveryone,
                        senderId: myId
                    });

                    if (_isCustomParticipantEveryone) {
                        dispatch(setLabelsVisible(true));
                    } else {
                        dispatch(setLabelsVisible(false));
                    }
                }
            };
        }

        // Initialize jQuery contextMenu
        $.contextMenu({
            selector,
            zIndex: 10000,
            items: contextMenuItems,
            events: {
                show(options) {
                    // Update pin text based on current state
                    if (options.items.pin) {
                        options.items.pin.name = t(
                            _isCustomParticipantVisible ? 'videothumbnail.unpinMe' : 'videothumbnail.pinMe'
                        );
                    }

                    // Update pin everyone text
                    if (options.items.pinEveryone) {
                        options.items.pinEveryone.name = t(
                            _isCustomParticipantEveryone ? 'videothumbnail.unpinEveryone' : 'videothumbnail.pinEveryone'
                        );
                    }
                }
            }
        });

        contextMenuInitialized.current = true;

        // Cleanup function
        return () => {
            if (contextMenuInitialized.current && typeof $ !== 'undefined' && typeof $.contextMenu === 'function') {
                try {
                    $.contextMenu('destroy', selector);
                    contextMenuInitialized.current = false;
                } catch {
                    // Ignore errors when destroying
                }
            }
        };
    }, [
        whiteBoardVisible,
        etherPadVisible,
        _isCustomParticipantVisible,
        _isCustomParticipantEveryone,
        _isPersonModerator,
        _visibleButtons,
        conference,
        participantID,
        IframeSharingParticipant?.moderatorPin,
        filmStripView,
        dispatch,
        t
    ]);


    return whiteBoardVisible || etherPadVisible ? (
        <div
            className = { `filmstrip__videos ${filmStripView === LAYOUTS.TILE_VIEW ? 'videocontainer' : ''}` }
            id = 'sharedThumbnailContainer'
            onClick = { _renderIframeThumbnail }
            ref = { containerRef }
            title = { fullDisplayName } >
            <div
                className = { `shared-thumbnail videocontainer ${_isCustomParticipantVisible || _isCustomParticipantEveryone ? 'videoContainerFocused' : ''}` }
                ref = { videoContainerRef } >
                <div
                    className = 'videocontainer__background'
                    style = { participantColor } />
                <div className = 'videocontainer__toptoolbar' >
                    { _renderTopIndicators }
                </div>
                <div className = 'avatar-container-custom-participnt'>
                    {etherPadVisible && <img
                        src = './static/assets/img/livepad.png'
                        width = '25%' />}
                    {whiteBoardVisible && <img
                        src = './static/assets/img/whiteboard.png'
                        width = '25%' />}
                </div>
                <div className = 'displayNameMainContainer'>
                    <div className = 'displayNameContainer'>
                        <span className = 'displayName'>
                            { displayLabel }
                        </span>
                    </div>
                </div>

                <span className = 'custom-participnt-dialog-parent' >
                    <span className = 'custom-participnt-parent'>
                        <Icon
                            size = '20'
                            src = { IconMenuThumb }
                            title = 'Remote user controls' />
                    </span>
                    <div className = 'custom-participnt-dialog'>{content}</div>
                </span>
            </div>
        </div>
    ) : null;


}

/**
 * Maps (parts of) the Redux state to the associated SharedThumbnail props.
 * @param {*} state - The Redux state.
 * @returns {Props}
 */
function mapStateToProps(state: any) {
    const { isCustomParticipantVisible, filmStripView, conference, isCustomParticipantEveryone } = state['features/base/conference'];
    const whiteBoardVisible = state['features/genericiframe'].visible;
    const etherPadVisible = state['features/etherpad'].editing;
    const whiteBoardParticipantId = state['features/genericiframe'].partId;
    const etherpadParticipantId = state['features/etherpad'].partId;
    const IframeSharingParticipant = getParticipantById(state, whiteBoardParticipantId || etherpadParticipantId);
    const isPictureInPictureEnabled = getPictureInPictureModeConfig(state);

    return {
        _isCustomParticipantVisible: isCustomParticipantVisible,
        whiteBoardVisible,
        etherPadVisible,
        filmStripView,
        IframeSharingParticipant,
        _isLocalParticipant: state['features/base/participants'].local,
        _isPersonModerator: isLocalParticipantModerator(state),
        _visibleButtons: visibleButton,
        _isCustomParticipantEveryone: isCustomParticipantEveryone,
        _isPictureInPictureEnabled: isPictureInPictureEnabled,
        whiteBoardParticipantId,
        etherpadParticipantId,
        conference
    };
}

export default connect(mapStateToProps)(translate(SharedThumbnail));
